import api from "../api.service"

/**
 * Sign-offs (api/signOff): one request for one person to sign off on one
 * thing. Writes go through these; the list is services/api/v2/signOff.js.
 */
class SignOffApi {
    /**
     * Asks for a sign-off.
     *
     * @param orgId {String}
     * @param data {{ type: String, refId: String, description?: String, approverId?: String, ready?: Boolean }}
     */
    request(orgId, data) {
        return api
            .post(`/org/${orgId}/signOff`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * One sign-off with its subject snapshot and what the caller may do
     * ({ ...signOff, subject, can: { edit, decide, withdraw, cancel } }).
     */
    get(orgId, signOffId) {
        return api
            .get(`/org/${orgId}/signOff/${signOffId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param data {{ description?: String, approverId?: String|null, ready?: Boolean }}
     *   ready again after a rejection resubmits it
     */
    update(orgId, signOffId, data) {
        return api
            .put(`/org/${orgId}/signOff/${signOffId}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * The approver's decision; a rejection needs the note.
     *
     * @param data {{ decision: "approved"|"rejected", note?: String }}
     */
    decide(orgId, signOffId, data) {
        return api
            .put(`/org/${orgId}/signOff/${signOffId}/decide`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /** The approver takes their approval back. */
    withdraw(orgId, signOffId, data = {}) {
        return api
            .put(`/org/${orgId}/signOff/${signOffId}/withdraw`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    cancel(orgId, signOffId) {
        return api
            .put(`/org/${orgId}/signOff/${signOffId}/cancel`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Live changes to the caller's sign-offs (the approver, the requester and
     * the project's people get them).
     *
     * @param operation {"create"|"update"} defaults to "create"
     * @param cb {function(Array<{ _id, doc, byRequestId }>)}
     * @return {{ disconnect: function }}
     */
    onChanges(operation, cb) {
        if (typeof operation === "function") {
            cb = operation
            operation = "create"
        }

        let socket
        let disconnected = false

        ;(async () => {
            socket = await api.socket.connect(`changes/signOffs/${operation}`)

            if (disconnected) {
                socket.disconnect()
                return
            }

            socket.on("changes", (changes) => cb(changes))
        })()

        return {
            disconnect: () => {
                disconnected = true
                socket?.disconnect()
            },
        }
    }
}

const signOffApi = new SignOffApi()

export default signOffApi
